/* ─── PALETTE ─────────────────────────────────────────────────────────────
   The background's value ramp, per sector.

   ── what the reference does ──

   Read its gradients as lightness, back to front:

     sky (bg_grad)   #FFDBA6  82%   warm, and it is the light source
     far hill        #d98981  69%
     grad2           #c46976  59%
     grad3 → grad4   #914d64  44%  →  #70375a  33%
     grad5 → grad7   #392e54  25%  →  #1c1b38  16%
     nearest hill    #0e0a1a   7%   near-black, and COLD

   Three rules are doing all the work, and none of them is the drawing:

     1. RANGE. Every surface is a different value, so depth is legible before
        a single shape has been identified.
     2. WARM TO COOL WITH DISTANCE. The near hill is not a dark version of the
        sky - it is a different hue. That is atmospheric perspective, and it
        is the part easiest to skip and most obvious when missing.
     3. SATURATION FALLS OFF AT BOTH ENDS. The horizon is the most saturated
        thing in the frame; sky above and ground in front are both muted.

   ── why this is built in HSL ──

   An earlier version mixed the sector accent toward black for the near
   layers. That keeps the accent's hue, so Utilities' amber produced a floor
   of mud — a dark orange, a colour nothing in the reference contains. Mixing
   toward a colour cannot rotate a hue; it can only darken one. Stating
   lightness, saturation and hue independently is the only way to get a ramp
   that goes warm-to-cool.
   ───────────────────────────────────────────────────────────────────────── */

/* Where every sector's foreground converges. Not black — a cold blue-black,
   the same decision as the reference's #0e0a1a. */
const COOL_HUE = 232;

function hexToHsl(hex) {
  const v = parseInt(hex.slice(1), 16);
  const r = ((v >> 16) & 255) / 255, g = ((v >> 8) & 255) / 255, b = (v & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h: h * 360, s, l };
}

function hslToHex(h, s, l) {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const seg = Math.floor(h / 60) % 6;
  const [r, g, b] = [
    [c, x, 0], [x, c, 0], [0, c, x], [0, x, c], [x, 0, c], [c, 0, x],
  ][seg];
  const to = v => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

/* Rotate a hue toward the cold end by `k`, the short way round the wheel —
   DAMPED BY LIGHTNESS, which is the part that was missing and the reason the
   floor turned pink.

   The reference rotates warm to cool over its hills, but it does that while
   dropping from 69% lightness to 7%. Hue and value fall together, so the
   magenta the rotation passes through (#914d64, #70375a) never reads as a
   colour - it reads as shadow.

   This ramp is LIGHT throughout: the floor sits at 81%. Take Industrial's red
   at h=0 and rotate it 30% toward 232 and the short way round is DOWNWARD,
   through 322 - magenta. At 7% lightness that is a shadow; at 81% it is
   bubblegum. Utilities' amber does the same thing and lands on pink.

   So the rotation is scaled by how far into shadow a surface actually is. A
   surface still near white is not in shadow and must keep its own hue; what
   makes it recede is losing SATURATION, which is what the ground stops above
   now do. Hue rotation is a dark-scene device and this is not a dark scene. */
function cool(h, k, l = 0) {
  const shade = Math.max(0, Math.min(1, (0.94 - l) / 0.55));
  let d = COOL_HUE - h;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return h + d * k * shade;
}

export function mixHex(a, b, k) {
  if (k <= 0) return a;
  if (k >= 1) return b;
  const pa = parseInt(a.slice(1), 16), pb = parseInt(b.slice(1), 16);
  const r = Math.round(((pa >> 16) & 255) + (((pb >> 16) & 255) - ((pa >> 16) & 255)) * k);
  const g = Math.round(((pa >> 8) & 255) + (((pb >> 8) & 255) - ((pa >> 8) & 255)) * k);
  const bl = Math.round((pa & 255) + ((pb & 255) - (pa & 255)) * k);
  return `#${((1 << 24) | (r << 16) | (g << 8) | bl).toString(16).slice(1)}`;
}

/* Each surface as [lightness, saturation, how far its hue has cooled].
   Reading down this table is reading the ramp: saturation peaking at the
   horizon, hue rotating steadily toward the cold end.

   These are the LIGHT values. The section sits between a light hero and a
   light footer; the robot arm standing on the floor is a black machine that
   loses its silhouette against a dark one; and the sector scene behind has to
   stay readable. The range lives in the SCENE — sharp and full — rather than
   in a slab of colour underneath it. */
const STOPS = {
  sky0:    [0.985, 0.30, 0.00],   // the header sits on this
  sky1:    [0.962, 0.42, 0.02],
  sky2:    [0.930, 0.56, 0.05],
  sky3:    [0.890, 0.68, 0.08],   // horizon, the most saturated thing here
  far:     [0.845, 0.50, 0.12],
  mid:     [0.812, 0.44, 0.18],
  near:    [0.788, 0.40, 0.24],
  /* The floor. It recedes by DRAINING, not by turning - see `cool` below for
     why a light surface must not rotate its hue. */
  ground0: [0.885, 0.30, 0.06],   // floor, just under the horizon
  ground1: [0.808, 0.17, 0.10],   // the near edge of the floor
};

export function ramp(accent) {
  const { h, s } = hexToHsl(accent);
  const out = {};
  for (const key of Object.keys(STOPS)) {
    const [l, sMul, k] = STOPS[key];
    out[key] = hslToHex(cool(h, k, l), Math.min(1, s * sMul + 0.06), l);
  }
  // The light source itself is the accent, undiluted — everything else in the
  // frame is lit BY it, so it is the one surface that is not on the ramp.
  out.glow0 = accent;
  out.glow1 = accent;
  return out;
}

/* The floor is LIGHT — 89% down to 81% — so copy sitting on it stays dark
   ink, the same as the rest of the site. Kept here, next to the values that
   decide it, rather than assumed in the stylesheet. */
export const FLOOR_IS_DARK = false;
